import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { LanguageSelector } from '../components/LanguageSelector';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moto-dashboard-4/artifacts/tifo19c3_grok_image_1769707994767.jpg";

const PAESI = [
  { code: 'CH', valuta: 'CHF' },
  { code: 'IT', valuta: 'EUR' },
  { code: 'DE', valuta: 'EUR' },
  { code: 'FR', valuta: 'EUR' },
  { code: 'AT', valuta: 'EUR' },
  { code: 'ES', valuta: 'EUR' },
  { code: 'NL', valuta: 'EUR' },
  { code: 'BE', valuta: 'EUR' },
  { code: 'PT', valuta: 'EUR' },
  { code: 'GB', valuta: 'GBP' },
  { code: 'US', valuta: 'USD' },
];

export default function Register() {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [paese, setPaese] = useState('CH');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const selectedPaese = PAESI.find(p => p.code === paese);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!nome || !email || !password || !paese) {
      toast.error(t('auth.fillAllFields'));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t('auth.passwordMismatch'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('auth.passwordTooShort'));
      return;
    }

    setLoading(true);
    try {
      await register(email, password, nome, paese);
      toast.success(t('auth.registerSuccess'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container relative">
      {/* Language Selector */}
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      
      <div className="auth-card chamfer">
        <div className="auth-logo">
          <div className="flex items-center justify-center mb-4">
            <div className="relative w-24 h-24 rounded-lg overflow-hidden">
              <img 
                src={LOGO_URL} 
                alt="Vault Ride" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <h1 className="auth-title">Vault Ride</h1>
          <p className="text-[#9CA3AF] text-xs uppercase tracking-widest mt-1">
            {t('auth.createAccount')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="form-group">
            <Label className="form-label">{t('auth.name')}</Label>
            <Input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder={t('auth.name')}
              className="form-input bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-12"
              data-testid="register-nome"
            />
          </div>

          <div className="form-group">
            <Label className="form-label">{t('auth.email')}</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              className="form-input bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-12 font-mono"
              data-testid="register-email"
            />
          </div>

          <div className="form-group">
            <Label className="form-label">{t('auth.country')}</Label>
            <Select value={paese} onValueChange={setPaese}>
              <SelectTrigger 
                className="bg-black/50 border-[#262626] rounded-none h-12"
                data-testid="register-paese"
              >
                <SelectValue placeholder={t('auth.country')} />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                {PAESI.map(p => (
                  <SelectItem key={p.code} value={p.code}>
                    {t(`countries.${p.code}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedPaese && (
              <p className="text-[#9CA3AF] text-xs mt-1">
                {t('auth.currency')}: <span className="text-[#FF5F1F]">{selectedPaese.valuta}</span>
              </p>
            )}
          </div>

          <div className="form-group">
            <Label className="form-label">{t('auth.password')}</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="form-input bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-12 font-mono"
              data-testid="register-password"
            />
          </div>

          <div className="form-group">
            <Label className="form-label">{t('auth.confirmPassword')}</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className="form-input bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-12 font-mono"
              data-testid="register-confirm-password"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm"
            data-testid="register-submit"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              t('auth.register')
            )}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-[#9CA3AF] text-sm">
            {t('auth.hasAccount')}{' '}
            <Link
              to="/login"
              className="text-[#FF5F1F] hover:underline uppercase tracking-wider text-xs"
              data-testid="login-link"
            >
              {t('auth.login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
